import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';

import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import InvoicesFileBlock from '@/pages/InvoiceList/components/InvoicesFileBlock';
import { ReceiverWrapper } from '@/pages/InvoiceList/components/InvoicesTableRow';
import { InvoiceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { renderCountryName, renderInvoiceAmount } from '@/utils/helpers';

interface Props {
  invoice: InvoiceResource;
}

const DeletedInvoicesTableRow: FC<Props> = ({ invoice }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const userCanManageData = useHasPermission('manage invoices');

  const onRestore = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to do restore this invoice?',
      );

      if (!confirmed) return;

      await api.post(`/v1/payments/invoices/restore/${invoice.id}`);

      queryClient.invalidateQueries(['invoices_list'], {
        exact: false,
      });

      queryClient.invalidateQueries(['deleted_invoices_list'], {
        exact: false,
      });

      alerts.success('Invoice has been successfully restored');
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>
        <ReceiverWrapper>
          <Text $type="smallHeadline">{invoice.member.name}</Text>
        </ReceiverWrapper>
      </Td>
      <Td>{invoice.comment ?? '-'}</Td>
      <Td>{invoice.company?.name ?? '-'}</Td>
      <Td>{invoice.bank.debtor_bank ?? '-'}</Td>
      <Td>{invoice.bank.bank ?? '-'}</Td>
      <Td>
        {invoice.bank.bank_country
          ? renderCountryName(invoice.bank.bank_country)
          : '-'}
      </Td>
      <Td>{invoice.bank.payment_type ?? '-'}</Td>
      <Td>{invoice.department?.name ?? '-'}</Td>
      <Td>{invoice.external_id}</Td>
      <Td>
        {invoice.finance_account_number_data
          ? invoice.finance_account_number_data.name
          : '-'}
      </Td>
      <Td>
        {invoice.currency === 'EUR'
          ? renderInvoiceAmount(invoice.amount, !!invoice.gpm)
          : '-'}
      </Td>
      <Td>
        {invoice.currency === 'USD'
          ? renderInvoiceAmount(invoice.amount, !!invoice.gpm)
          : '-'}
      </Td>
      <Td>
        {invoice.currency === 'GBP'
          ? renderInvoiceAmount(invoice.amount, !!invoice.gpm)
          : '-'}
      </Td>
      <Td>{DateTime.fromISO(invoice.created_at).toFormat('dd/LL/yyyy t')}</Td>
      <Td>
        {invoice.deleted_at
          ? DateTime.fromISO(invoice.deleted_at).toFormat('dd/LL/yyyy t')
          : '-'}
      </Td>
      <Td>{invoice.creator ?? '-'}</Td>
      <Td>{invoice.edited_by ?? '-'}</Td>
      <Td>
        {invoice.file ? (
          <InvoicesFileBlock invoiceId={invoice.id} file={invoice.file} />
        ) : (
          '-'
        )}
      </Td>
      {userCanManageData && (
        <Td $actionsCol>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onRestore}>
              <span>
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.02387 5.82433H1.24609V2.13202"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.91739 12.7866C4.88599 13.7333 6.12008 14.378 7.46357 14.6392C8.80707 14.9004 10.1996 14.7663 11.4652 14.254C12.7307 13.7416 13.8124 12.874 14.5735 11.7608C15.3345 10.6476 15.7407 9.33884 15.7407 8.00001C15.7407 6.66118 15.3345 5.35242 14.5735 4.23922C13.8124 3.12603 12.7307 2.2584 11.4652 1.74605C10.1996 1.23371 8.80707 1.09965 7.46357 1.36085C6.12008 1.62204 4.88599 2.26674 3.91739 3.21344L1.24609 5.82429"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Restore
            </ActionsDropdownButton>
          </ActionsDropdown>
        </Td>
      )}
    </tr>
  );
};

export default DeletedInvoicesTableRow;
